import {
  DECISION_MANUAL_STATUSES,
  DECISION_STATUS_LABEL,
  DECISION_TIPOS,
  decisionStatusLabel,
  type DecisionStatus,
  type DecisionTipo,
} from '@supply/contracts'
import { ClipboardPlus, Ellipsis, Gavel, ListChecks, ListTodo, Replace, SquareArrowOutUpRight, Trash2 } from 'lucide-react'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { DecisionStatusDot, DecisionStatusPill, DecisionTipoBadge, decisionToneClass } from '@/components/domain'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { convertDecision, deleteDecision, substituteDecision, updateDecision } from '@/data/api'
import type { Decision } from '@/data/types'
import { invalidate } from '@/data/use-load'
import { useWrite } from '@/lib/use-write'
import { cn } from '@/lib/utils'

/**
 * Status da decisão como pílula com bolinha; editável vira select com a mesma
 * cara. Registro só vai de Vigente para Revogada (e volta).
 */
export function DecisionStatusSelect({ decision, canEdit, className }: { decision: Decision; canEdit: boolean; className?: string }) {
  const { run, pending } = useWrite()
  const tipo = decision.tipo
  if (!canEdit || decision.status === 'convertida') return <DecisionStatusPill status={decision.status} tipo={tipo} className={className} />
  return (
    <Select
      value={decision.status}
      disabled={pending}
      onValueChange={(v) => void run(updateDecision(decision.id, { status: v as DecisionStatus })).then((r) => r.ok && invalidate())}
    >
      <SelectTrigger
        aria-label="Status da decisão"
        className={cn(
          'h-7 w-auto gap-1.5 rounded-sm border-transparent px-2 text-xs font-medium shadow-none hover:brightness-110 focus:ring-1 focus:ring-offset-0 [&>svg]:h-3.5 [&>svg]:w-3.5',
          decisionToneClass(decision.status, tipo),
          className,
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {DECISION_MANUAL_STATUSES[tipo].map((s) => (
          <SelectItem key={s} value={s} className="text-xs">
            <span className="flex items-center gap-2">
              <DecisionStatusDot status={s} tipo={tipo} />
              {decisionStatusLabel(tipo, s, DECISION_STATUS_LABEL)}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Seletor do tipo (A executar | Registro) com a cara do badge — ATA e formulários. */
export function DecisionTipoSelect({
  value,
  onChange,
  disabled,
  label = 'Tipo da decisão',
  className,
}: {
  value: DecisionTipo
  onChange: (tipo: DecisionTipo) => void
  disabled?: boolean
  label?: string
  className?: string
}) {
  if (disabled) return <DecisionTipoBadge tipo={value} className={className} />
  return (
    <Select value={value} onValueChange={(v) => onChange(v as DecisionTipo)}>
      <SelectTrigger
        aria-label={label}
        className={cn('h-7 w-auto gap-1 border-transparent px-1 text-xs shadow-none focus:ring-1 focus:ring-offset-0 [&>svg]:h-3.5 [&>svg]:w-3.5', className)}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {DECISION_TIPOS.map((t) => (
          <SelectItem key={t} value={t} className="text-xs">
            <DecisionTipoBadge tipo={t} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Rota da ATA de origem (`/reuniao` ou `/reuniao-ras`), quando a lista trouxe a ATA. */
export function ataOriginHref(d: Decision): string | null {
  if (!d.ata) return null
  return `${d.ata.tipo === 'ras' ? '/reuniao-ras' : '/reuniao'}?semana=${d.ata.semana}&area=${d.ata.area_id ?? 'geral'}`
}

/**
 * Menu "⋯" da decisão: converter em tarefa/ação, mudar o tipo, substituir um
 * registro por outro, abrir a origem (tarefa ou ATA), excluir.
 */
export function DecisionActions({
  decision: d,
  canEdit,
  onOpenOrigin,
}: {
  decision: Decision
  canEdit: boolean
  /** Mostra "Abrir tarefa de origem" (na tela de Decisões; dentro da tarefa não faz sentido). */
  onOpenOrigin?: () => void
}) {
  const { run, pending } = useWrite()
  const navigate = useNavigate()
  const { confirm, confirmDialog } = useConfirm()
  const [substituting, setSubstituting] = React.useState(false)
  const open = d.status === 'aberta'
  const canConvert = canEdit && open && d.tipo === 'executar'
  const ataHref = onOpenOrigin ? null : ataOriginHref(d)
  if (!canEdit && !onOpenOrigin && !ataHref) return null

  async function switchTipo() {
    const to = d.tipo === 'registro' ? 'executar' : 'registro'
    if (to === 'registro' && (d.owner_id || d.owner_text || d.prazo)) {
      const ok = await confirm({
        title: 'Virar registro?',
        description: 'Registro não tem responsável nem prazo: os dois serão apagados desta decisão.',
        confirmLabel: 'Virar registro',
      })
      if (!ok) return
    }
    const r = await run(updateDecision(d.id, { tipo: to }), { success: to === 'registro' ? 'Decisão virou registro' : 'Decisão virou A executar' })
    if (r.ok) invalidate()
  }

  async function convert(target: 'task' | 'action') {
    const r = await run(convertDecision(d.id, target), {
      success: target === 'task' ? 'Decisão convertida em tarefa' : 'Decisão convertida em ação do checklist',
    })
    if (r.ok) {
      invalidate()
      if (target === 'task' && r.value.key) void navigate(`/quadro?tarefa=${r.value.key}`)
    }
  }

  async function remove() {
    const ok = await confirm({ title: 'Excluir decisão?', description: d.decisao, destructive: true, confirmLabel: 'Excluir' })
    if (!ok) return
    const r = await run(deleteDecision(d.id), { success: 'Decisão excluída' })
    if (r.ok) invalidate()
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" className="size-9 text-muted-foreground hover:text-foreground md:size-8" aria-label="Ações da decisão" disabled={pending}>
            <Ellipsis />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          {canConvert && (
            <>
              <DropdownMenuItem onSelect={() => void convert('task')}>
                <ClipboardPlus /> Converter em tarefa
              </DropdownMenuItem>
              {d.task_id && (
                <DropdownMenuItem onSelect={() => void convert('action')}>
                  <ListChecks /> Converter em ação da tarefa
                </DropdownMenuItem>
              )}
            </>
          )}
          {canEdit && open && (
            <DropdownMenuItem onSelect={() => void switchTipo()}>
              {d.tipo === 'registro' ? <ListTodo /> : <Gavel />} {d.tipo === 'registro' ? 'Mudar para A executar' : 'Mudar para Registro'}
            </DropdownMenuItem>
          )}
          {canEdit && open && d.tipo === 'registro' && (
            <DropdownMenuItem onSelect={() => setSubstituting(true)}>
              <Replace /> Substituir por outra decisão…
            </DropdownMenuItem>
          )}
          {onOpenOrigin && (
            <DropdownMenuItem onSelect={onOpenOrigin}>
              <SquareArrowOutUpRight /> Abrir tarefa de origem
            </DropdownMenuItem>
          )}
          {ataHref && (
            <DropdownMenuItem onSelect={() => void navigate(ataHref)}>
              <SquareArrowOutUpRight /> Abrir ATA de origem
            </DropdownMenuItem>
          )}
          {canEdit && (
            <>
              {(canConvert || open || onOpenOrigin || ataHref) && <DropdownMenuSeparator />}
              <DropdownMenuItem onSelect={() => void remove()} className="text-destructive focus:bg-destructive/10 focus:text-destructive">
                <Trash2 /> Excluir
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {confirmDialog}
      <SubstituteDialog decision={d} open={substituting} onOpenChange={setSubstituting} />
    </>
  )
}

/** Revoga o registro vigente e registra o novo no lugar (mesma origem). */
function SubstituteDialog({ decision: d, open, onOpenChange }: { decision: Decision; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { run, pending } = useWrite()
  const [text, setText] = React.useState('')
  React.useEffect(() => {
    if (open) setText('')
  }, [open])
  const ready = text.trim().length > 0 && text.trim().length <= 1000
  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!ready) return
    const r = await run(substituteDecision(d.id, text.trim()), { success: 'Registro substituído' })
    if (r.ok) {
      onOpenChange(false)
      invalidate()
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Substituir registro</DialogTitle>
            <DialogDescription>
              “{d.decisao}” fica revogada, com link para a nova. A nova nasce vigente, com a mesma origem.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor={`sub-${d.id}`}>Nova decisão</Label>
            <Textarea id={`sub-${d.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={1000} autoFocus />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!ready || pending}>
              Substituir
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
